/**
 * Structured Content Renderer for Blog V1
 * Renders content_sections with backward compatibility for legacy content
 */

import React, { useState } from 'react';
import { renderTipTapToHTML } from '../../utils/tiptapRenderer';
import { extractPlainText } from '../../utils/tiptapPlainText';
import { getTitlePlainText, getTitleHtml } from '../../utils/titleFormat';

export const StructuredContentRenderer = ({ contentSections = [], legacyContent = null }) => {
  // Hooks must be called before any early returns
  const [sectionStates, setSectionStates] = useState({});
  const [faqStates, setFaqStates] = useState({});

  // If no structured sections, render legacy content
  if (!contentSections || contentSections.length === 0) {
    if (legacyContent && typeof legacyContent === 'object') {
      return (
        <div dangerouslySetInnerHTML={{ __html: renderTipTapToHTML(legacyContent) }} />
      );
    }
    return null;
  }

  const toggleSection = (sectionId) => {
    setSectionStates(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const toggleFaq = (faqKey) => {
    setFaqStates(prev => ({ ...prev, [faqKey]: !prev[faqKey] }));
  };

  const getDefaultOpenState = (section) => {
    if (section.default_open !== undefined) {
      return section.default_open;
    }
    // Intent-based defaults
    const closedByDefault = ['example', 'faq', 'reference'];
    return !closedByDefault.includes(section.intent);
  };

  const renderBlock = (block, blockIndex, sectionId) => {
    switch (block.type) {
      case 'paragraph':
        if (block.content?.format === 'tiptap' && block.content?.json) {
          return (
            <div
              key={blockIndex}
              className="mb-4"
              dangerouslySetInnerHTML={{ __html: renderTipTapToHTML(block.content.json) }}
            />
          );
        }
        return null;

      case 'bullet_list':
        return (
          <ul key={blockIndex} className="list-disc list-inside space-y-2 mb-4 ml-4">
            {(block.items || []).map((item, itemIndex) => (
              <li key={itemIndex}>{item}</li>
            ))}
          </ul>
        );

      case 'numbered_list':
        return (
          <ol key={blockIndex} className="list-decimal list-inside space-y-2 mb-4 ml-4">
            {(block.items || []).map((item, itemIndex) => (
              <li key={itemIndex}>{item}</li>
            ))}
          </ol>
        );

      case 'faq':
        const faqBlockKey = `${sectionId}-faq-${blockIndex}`;
        const faqOpen = faqStates[faqBlockKey] !== undefined ? faqStates[faqBlockKey] : false;
        const questionHtml = block.question?.format === 'tiptap' && block.question?.json 
          ? renderTipTapToHTML(block.question.json)
          : (block.question || 'Question');
        return (
          <div key={blockIndex} className="border border-[#E5E7EB] rounded-lg mb-4" itemScope itemType="https://schema.org/Question">
            <button
              onClick={() => toggleFaq(faqBlockKey)}
              aria-expanded={faqOpen}
              aria-controls={`faq-answer-${faqBlockKey}`}
              className="w-full px-4 py-3 text-left flex justify-between items-center hover:bg-[#F9FAFB]"
            >
              <strong 
                className="font-semibold flex-1"
                itemProp="name"
                dangerouslySetInnerHTML={{ __html: questionHtml }}
              />
              <svg
                className={`w-5 h-5 text-[#6B7280] transition-transform flex-shrink-0 ml-2 ${faqOpen ? 'transform rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {faqOpen && (
              <div id={`faq-answer-${faqBlockKey}`} className="px-4 py-3 border-t border-[#E5E7EB]" itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                {block.answer?.format === 'tiptap' && block.answer?.json ? (
                  <div itemProp="text" dangerouslySetInnerHTML={{ __html: renderTipTapToHTML(block.answer.json) }} />
                ) : (
                  <p itemProp="text">{block.answer || ''}</p>
                )}
              </div>
            )}
          </div>
        );

      case 'callout':
        const variantStyles = {
          info: 'bg-blue-50 border-blue-200 text-blue-800',
          tip: 'bg-green-50 border-green-200 text-green-800',
          warning: 'bg-amber-50 border-amber-200 text-amber-800'
        };
        const style = variantStyles[block.variant] || variantStyles.info;
        return (
          <div key={blockIndex} className={`border-l-4 p-4 rounded-r-lg mb-4 ${style}`}>
            {block.content?.format === 'tiptap' && block.content?.json ? (
              <div dangerouslySetInnerHTML={{ __html: renderTipTapToHTML(block.content.json) }} />
            ) : (
              <div>{block.content || ''}</div>
            )}
          </div>
        );

      case 'image':
        const isDecorative = !block.alt || block.alt.trim() === '';
        return (
          <figure key={blockIndex} className="my-6">
            <img
              src={block.url || ''}
              alt={block.alt || ''}
              className="w-full h-auto rounded-lg"
              aria-hidden={isDecorative}
            />
            {block.caption && (
              <figcaption className="text-center text-sm text-[#6B7280] mt-2">
                {block.caption}
              </figcaption>
            )}
          </figure>
        );

      case 'table':
        return (
          <div key={blockIndex} className="overflow-x-auto mb-4">
            <table className="min-w-full divide-y divide-[#E5E7EB]">
              <thead className="bg-[#F9FAFB]">
                <tr>
                  {(block.headers || []).map((header, headerIndex) => (
                    <th key={headerIndex} className="px-4 py-2 text-left text-sm font-semibold text-[#111827]">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E5E7EB]">
                {(block.rows || []).map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {(row.cells || row || []).map((cell, cellIndex) => (
                      <td key={cellIndex} className="px-4 py-2 text-sm text-[#374151]">
                        {Array.isArray(cell) ? cell.join(', ') : cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-8">
      {contentSections.map((section) => {
        const sectionId = section.id || `section-${Math.random()}`;
        const isOpen = sectionStates[sectionId] !== undefined
          ? sectionStates[sectionId]
          : getDefaultOpenState(section);

        return (
          <section key={sectionId} id={sectionId} data-section-id={sectionId} className="border border-[#E5E7EB] rounded-lg overflow-hidden">
            {section.heading && (
              <div className="bg-[#F9FAFB] hover:bg-[#F0F4F8] transition-colors">
                <button
                  onClick={() => toggleSection(sectionId)}
                  aria-expanded={isOpen}
                  aria-controls={`section-content-${sectionId}`}
                  className="w-full px-6 py-4 text-left flex justify-between items-center"
                >
                  <h2 
                    className="text-xl font-bold text-[#111827] flex-1"
                    dangerouslySetInnerHTML={{ 
                      __html: typeof section.heading === 'string' 
                        ? section.heading 
                        : (getTitleHtml(section.heading) || (section.heading?.type === 'doc' ? renderTipTapToHTML(section.heading) : ''))
                    }}
                  />
                  <svg
                    className={`w-5 h-5 text-[#6B7280] transition-transform flex-shrink-0 ml-2 ${isOpen ? 'transform rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            )}
            {isOpen && (
              <div id={`section-content-${sectionId}`} className="p-6 space-y-4">
                {(section.blocks || []).map((block, blockIndex) => renderBlock(block, blockIndex, sectionId))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default StructuredContentRenderer;
